import React, { useContext, useEffect, useRef, useState } from "react";
import {
  AddressBookAutoFillType,
  AddressBookEntry,
  EndCustomer,
  Maybe,
  useSearchFromAddressBookLazyQuery,
} from "@api/graphql/generated/generated-types";
import {
  Controller,
  FieldValues,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { Combobox, Label } from "@src/common/components";
import { useDebounce } from "usehooks-ts";
import { StopValueProps } from "../hooks/useAutoFill";
import {
  camelCaseToHumanized,
  formatPhoneNumber,
} from "@src/common/lib/textUtils";
import { appendUniqueEmailRecipients } from "@src/common/lib/emailUtils";
import { LoadingSpinner } from "baseui/button/styled-components";
import { AuthContext } from "@src/auth/components/AuthProvider";
import { UserIcon } from "@heroicons/react/24/solid";
import { GlobeAltIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import { useAuth } from "@src/common/hooks/useAuth";

type SearchType = "contactName" | "companyName" | "phone";

interface SuggestionInputProps {
  control: any;
  type: SearchType;
  secondaryType?: SearchType;
  selectedCustomer: EndCustomer | undefined;
  setValue: UseFormSetValue<FieldValues>;
  getValues?: UseFormGetValues<FieldValues>;
  clearErrors: UseFormClearErrors<FieldValues>;
  shipmentStopsFormName: string;
  isStopFullUpdate?: boolean;
  usePositive?: boolean;
  required?: boolean;
}

function SuggestionInput({
  control,
  type,
  secondaryType,
  selectedCustomer,
  setValue,
  getValues,
  clearErrors,
  shipmentStopsFormName,
  isStopFullUpdate = false,
  usePositive = true,
  required = false,
}: SuggestionInputProps) {
  const { courierId, courier } = useAuth();
  const inputRef = useRef<HTMLElement>(null);
  const [query, setQuery] = useState("");
  const [options, setOptions] = React.useState<AddressBookEntry[]>([]);
  const debouncedQueryValue = useDebounce<string>(query, 500);
  const addressBookAutoFillType = courier?.courier?.addressBookAutoFillType;

  const [SearchFromAddressBook, { loading }] =
    useSearchFromAddressBookLazyQuery({
      fetchPolicy: "network-only",
    });

  const getValueToSearch = (typeToMatch: SearchType) => {
    if (type === typeToMatch || secondaryType === typeToMatch) {
      return debouncedQueryValue;
    }
    return undefined;
  };

  useEffect(() => {
    if (debouncedQueryValue.length > 0 && courierId) {
      const variables = {
        courierId: courierId,
        endCustomerIdBias: selectedCustomer?.id || undefined,
        contactNameSearch: getValueToSearch("contactName"),
        companyNameSearch: getValueToSearch("companyName"),
        phoneSearch: getValueToSearch("phone"),
      };
      SearchFromAddressBook({ variables }).then((res) => {
        const entries: AddressBookEntry[] =
          (res.data?.searchFromAddressBook as AddressBookEntry[]) || [];
        setOptions(entries);
      });
    }
  }, [
    debouncedQueryValue,
    courierId,
    selectedCustomer,
    type,
    SearchFromAddressBook,
  ]);

  const handleChange = (
    nextValue: string,
    addressBookEntry?: AddressBookEntry | null,
  ) => {
    if (type === "phone") {
      nextValue = formatPhoneNumber(nextValue);
    }
    setQuery(nextValue);
    if (addressBookEntry) {
      const {
        streetAddress = "",
        googlePlaceId = "",
        city = "",
        country = "",
        state = "",
        zipCode = "",
      } = addressBookEntry.location;
      const suite = addressBookEntry.suite;
      const contactName = addressBookEntry.contactName;
      const contactPhoneNumber: string = addressBookEntry.phone
        ? formatPhoneNumber(addressBookEntry.phone)
        : "";
      const companyName: Maybe<string> | undefined =
        addressBookEntry.companyName;
      const instruction: Maybe<string> | undefined =
        addressBookEntry.instruction;
      const locationId = addressBookEntry.location.id;
      const lngLat = addressBookEntry.location.lngLat;
      let fullAddress = `${streetAddress}, ${city}, ${state}, ${country} ${zipCode}`;
      let valuesToSetInForm: any = {
        streetAddress: fullAddress,
        googlePlaceId,
        suite,
        contactName: contactName || "",
        phone: contactPhoneNumber,
        companyName: companyName || "",
        city,
        country,
        state,
        instruction:
          addressBookAutoFillType ===
          AddressBookAutoFillType.ExcludeInstructions
            ? undefined
            : instruction,
        locationId: locationId ? locationId : undefined,
        lngLat,
      };
      if (isStopFullUpdate) {
        valuesToSetInForm = {
          streetAddress: fullAddress,
          companyName: companyName || "",
          contactName: contactName || "",
          googlePlaceId,
          phone: contactPhoneNumber,
          suite,
          instruction:
            addressBookAutoFillType ===
            AddressBookAutoFillType.ExcludeInstructions
              ? undefined
              : instruction,
          locationId: locationId ? locationId : undefined,
          lngLat,
        };
      }
      setStopValues(valuesToSetInForm);

      if (getValues) {
        const currentEmailRecipients: string =
          getValues("additionalEmailRecipients") || "";
        const updatedEmailRecipients = appendUniqueEmailRecipients(
          currentEmailRecipients,
          addressBookEntry.emailRecipients as string[] | undefined
        );
        if (updatedEmailRecipients !== currentEmailRecipients) {
          setValue("additionalEmailRecipients", updatedEmailRecipients);
        }
      }

      clearErrors(`${shipmentStopsFormName}.${type}`);
      clearErrors(`${shipmentStopsFormName}.streetAddress`);
    } else {
      setValue(`${shipmentStopsFormName}.${type}`, nextValue);
    }
  };

  const mapOptionToString = (
    typeToShow: SearchType,
    option: AddressBookEntry
  ): string => {
    switch (typeToShow) {
      case "contactName":
        return option.contactName || "";
      case "companyName":
        return option.companyName || "";
      case "phone":
        return option.phone ? formatPhoneNumber(option.phone) : "";
      default:
        return "";
    }
  };

  const setStopValues = (values: StopValueProps) => {
    Object.keys(values).forEach((key) => {
      setValue(
        `${shipmentStopsFormName}.${key}`,
        values[key as keyof StopValueProps]
      );
    });
  };

  const displayValue = (option: AddressBookEntry) => {
    const primaryDisplay = mapOptionToString(type, option);
    const secondaryDisplay =
      secondaryType && mapOptionToString(secondaryType, option);
    if (!primaryDisplay && !secondaryDisplay) {
      return "";
    }
    if (!primaryDisplay) {
      return `[${secondaryDisplay}]`;
    }
    if (!secondaryDisplay) {
      return primaryDisplay;
    }
    return `${primaryDisplay} [${secondaryDisplay}]`;
  };

  const SuggestionInputListItem: React.FC<{
    isSelected: boolean;
    option: unknown;
  }> = ({ isSelected, option }) => {
    const addressBookEntry = option as AddressBookEntry; // Type assertion

    return (
      <div
        className={classNames({
          "flex justify-between w-full hover:cursor-pointer": true,
          "opacity-50": addressBookEntry.endCustomerId !== selectedCustomer?.id,
        })}
      >
        <div className="w-4/5 flex flex-col">
          <div className="text-xs font-bold truncate dark:text-gray-100">
            {displayValue(addressBookEntry)}
          </div>
          <div className="text-xs dark:text-gray-300">
            {addressBookEntry.location.streetAddress}
            {addressBookEntry.suite && `, ${addressBookEntry.suite}`}
          </div>
        </div>
        <div className="w-1/5">
          {addressBookEntry.endCustomerId === selectedCustomer?.id ? (
            <UserIcon className="w-5 h-5 text-green-500" />
          ) : (
            <GlobeAltIcon className="w-5 h-5 text-blue-500" />
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Label className="text-xs font-medium text-gray-700 dark:text-gray-300 flex justify-between truncate">
        <div>
          {camelCaseToHumanized(type)}
          {required ? (
            <span className="text-red-500"> *</span>
          ) : (
            <span className="text-slate-400 dark:text-gray-500 text-xs">
              {" "}
              (Optional)
            </span>
          )}
        </div>
        <span>
          <LoadingSpinner $style={{ display: loading ? "block" : "none" }} />
        </span>
      </Label>
      <Controller
        name={`${shipmentStopsFormName}.${type}`}
        control={control}
        rules={{
          required: required
            ? `${camelCaseToHumanized(type)} is required`
            : false,
        }}
        render={({ fieldState: { error }, field: { ref, value } }) => {
          ref(inputRef.current);
          if (value === undefined) {
            setQuery("");
          }
          return (
            <Combobox
              size="mini"
              inputRef={inputRef}
              error={!!error}
              onChange={(b, a) => handleChange(b, a as any)}
              mapOptionToString={(a) => mapOptionToString(type, a as any)}
              value={value}
              name={`${type}-options`}
              options={options}
              mapOptionToNode={SuggestionInputListItem}
              overrides={{
                Input: {
                  props: {
                    placeholder: selectedCustomer
                      ? `Search for a ${camelCaseToHumanized(type)}`
                      : "",
                    error: !!error,
                    autoComplete: "off",
                    type: type === "phone" ? "tel" : "text",
                    inputMode: type === "phone" ? "tel" : undefined,
                  },
                },
                ListItem: {
                  style: {
                    paddingTop: "5px",
                    paddingBottom: "5px",
                    height: "auto",
                  },
                },
              }}
            />
          );
        }}
      />
    </>
  );
}

export default SuggestionInput;
